import { useCallback, useEffect, useRef, useState } from 'react';
import { ICON_BUTTON_SIZE } from '../components/touch-target';
import { usePopoverClamp } from '../components/usePopoverClamp';
import {
  ArrowPathIcon,
  BellIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { Job, JobStatus, JobType } from '@xphoto/shared';
import { isRetryableJobType } from '@xphoto/shared';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { useCanWrite } from '../auth';
import { ApiError } from '../gallery/api';
import { interpolate } from '../gallery/format';
import { cancelJob, retryJob } from './jobs-api';
import { etaParts } from './format-eta';
import { clock } from './format-time';
import { formatJobResult } from './format-job-result';
import { aiTagHasProposals, isDoneButAllFailed, scanFoundChanges } from './job-activity';
import { useJobActivity } from './use-job-activity';

/** Clé i18n du libellé d'un type de job. */
const TYPE_LABEL: Record<JobType, MessageKey> = {
  scan: 'notifications.typeScan',
  thumbnail: 'notifications.typeThumbnail',
  ai_tag: 'notifications.typeAiTag',
  dedup: 'notifications.typeDedup',
  dedup_apply: 'notifications.typeDedupApply',
  dedup_exact_apply: 'notifications.typeDedupExactApply',
  exif_publish: 'notifications.typeExifPublish',
  exif_erase: 'notifications.typeExifErase',
  move: 'notifications.typeMove',
  compress: 'notifications.typeCompress',
  rotate: 'notifications.typeRotate',
  originals_cleanup: 'notifications.typeOriginalsCleanup',
  trash_purge: 'notifications.typeTrashPurge',
  rename_extensions: 'notifications.typeRenameExtensions',
  empty_folders_cleanup: 'notifications.typeEmptyFoldersCleanup',
};

/** Clé i18n du libellé d'un statut. */
const STATUS_LABEL: Record<JobStatus, MessageKey> = {
  queued: 'notifications.statusQueued',
  running: 'notifications.statusRunning',
  done: 'notifications.statusDone',
  failed: 'notifications.statusFailed',
  canceled: 'notifications.statusCanceled',
};

/** Icône de statut terminal (icône + texte, jamais la couleur seule). */
const STATUS_ICON: Record<'done' | 'failed' | 'canceled', typeof CheckCircleIcon> = {
  done: CheckCircleIcon,
  failed: ExclamationTriangleIcon,
  canceled: NoSymbolIcon,
};

/** Teinte de renfort du statut terminal (jamais porteuse seule de l'information). */
const STATUS_TONE: Record<'done' | 'failed' | 'canceled', string> = {
  done: 'text-brand',
  failed: 'text-danger',
  canceled: 'text-muted',
};

/**
 * Cloche de notifications de l'en-tête : affiche les jobs asynchrones (scan,
 * vignettes, EXIF…) EN COURS et TERMINÉS (< 24 h), avec ETA et progression.
 *
 * Popover ancré SOUS la cloche via un wrapper `relative` + panneau `absolute`
 * (même pattern qu'`AccountSwitcher`) : cela évite le piège du `backdrop-blur` de
 * l'en-tête (un enfant `position: fixed` s'y calerait sur la boîte de l'en-tête
 * au lieu du viewport) SANS recourir à un portail. Accessible : Échap ferme
 * (focus rendu au déclencheur), clic extérieur ferme.
 *
 * Ce panneau portait `max-w-[calc(100vw-1rem)]`, seule protection d'écran étroit du
 * projet — retirée en v1.40.1 : elle bornait la LARGEUR sans rien dire de la POSITION,
 * et ne suffisait ici que parce que CE déclencheur est collé au bord de l'en-tête.
 * `usePopoverClamp` fait désormais les deux, pour les huit popovers à la fois.
 */
export function NotificationBell({
  libraryId,
  onReviewAiTags,
  onRefreshGallery,
}: {
  libraryId: string;
  /** Pont « Réviser les tags » : appelé depuis une ligne `ai_tag` terminée avec des
   *  propositions (ouvre la galerie « Non taguées »). Absent → aucune action proposée. */
  onReviewAiTags?: () => void;
  /** Pont « Actualiser la galerie » : appelé depuis une ligne `scan` terminée ayant
   *  indexé/retiré des fichiers (recharge la galerie). Absent → aucune action proposée. */
  onRefreshGallery?: () => void;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const { active, recent, activeCount, status, dismiss, dismissAll } = useJobActivity(libraryId);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  /** Maintien dans le cadre visible (remplace l'ancien `max-w-[calc(100vw-1rem)]`). */
  const panelRef = usePopoverClamp(open);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  // Ferme le popover PUIS déclenche la révision (navigation) — la cloche persiste dans
  // la coquille, elle resterait ouverte sinon.
  const handleReview = useCallback(() => {
    close(false);
    onReviewAiTags?.();
  }, [close, onReviewAiTags]);

  // Ferme le popover PUIS déclenche le rechargement de la galerie (la cloche persiste
  // dans la coquille, elle resterait ouverte sinon).
  const handleRefresh = useCallback(() => {
    close(false);
    onRefreshGallery?.();
  }, [close, onRefreshGallery]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const hasRecent = recent.length > 0;
  const isEmpty = active.length === 0 && !hasRecent;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={interpolate(t('notifications.open'), { count: activeCount }, locale)}
        title={t('notifications.title')}
        onClick={() => setOpen((o) => !o)}
        className={`relative flex h-11 w-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
          open ? 'bg-surface-2 text-fg outline-2 outline-offset-2 outline-brand' : ''
        }`}
      >
        <BellIcon className="h-6 w-6" aria-hidden="true" />
        {activeCount > 0 && (
          <span
            className="absolute right-1 top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-brand px-1 py-px text-[0.625rem] font-semibold leading-none text-brand-fg"
            aria-hidden="true"
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('notifications.title')}
          className="absolute end-0 z-20 mt-1 w-80 rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {/* Titre + « Tout effacer » (masque tous les terminés). */}
          <div className="flex items-center gap-2 px-3 py-2">
            <p className="flex-1 text-xs font-medium text-muted">{t('notifications.title')}</p>
            {hasRecent && (
              <button
                type="button"
                onClick={dismissAll}
                className="rounded-md px-2 py-1 text-xs text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
              >
                {t('notifications.clearAll')}
              </button>
            )}
          </div>

          {status === 'error' && (
            <p role="alert" className="px-3 py-2 text-sm text-danger">
              {t('notifications.error')}
            </p>
          )}

          {status === 'loading' && active.length === 0 && !hasRecent && (
            <p className="px-3 py-6 text-center text-sm text-muted" role="status">
              {t('notifications.loading')}
            </p>
          )}

          {status !== 'loading' && isEmpty && status !== 'error' && (
            <p className="px-3 py-6 text-center text-sm text-muted">{t('notifications.empty')}</p>
          )}

          {/* Jobs actifs (progression + ETA + annulation). */}
          {active.map((job) => (
            <ActiveRow key={job.id} job={job} />
          ))}

          {active.length > 0 && hasRecent && <div className="my-1 border-t border-border" />}

          {/* Jobs terminés récents (icône de statut + texte + supprimer). */}
          {recent.map((job) => (
            <RecentRow
              key={job.id}
              job={job}
              onDismiss={() => dismiss(job.id)}
              onReview={onReviewAiTags && aiTagHasProposals(job) ? handleReview : undefined}
              onRefresh={onRefreshGallery && scanFoundChanges(job) ? handleRefresh : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Ligne d'un job actif : type, statut, barre de progression, ETA, annuler. */
function ActiveRow({ job }: { job: Job }): React.JSX.Element {
  const { t, locale } = useI18n();
  // La cloche est montée SANS CONDITION (`AppChrome`) et le flux SSE est cloisonné en
  // LECTURE : un utilisateur en lecture seule VOIT donc les jobs — c'est voulu. Mais
  // l'ANNULATION est une écriture (403 côté serveur) : le bouton est GARDÉ par le rôle
  // (masqué, pas seulement désactivé — cf. la discipline `useCanWrite`).
  const canWrite = useCanWrite();
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const determinate = job.total > 0;
  const percent = determinate ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;
  const started = clock(job.startedAt);

  const eta = etaParts(job.etaSeconds, {
    secondsTemplate: t('notifications.etaSeconds'),
    minutesTemplate: t('notifications.etaMinutes'),
  });
  const etaLabel = eta ? interpolate(eta.template, { value: eta.value }) : null;

  // Échec d'annulation : PLUS JAMAIS silencieux (audit F8). Avant, le bouton se
  // réactivait sans le moindre message — un 403 (lecture seule) était indiscernable
  // d'un clic sans effet.
  const onCancel = (): void => {
    if (canceling) return;
    setCancelError(null);
    setCanceling(true);
    void cancelJob(job.id).catch((err: unknown) => {
      setCanceling(false);
      setCancelError(
        err instanceof ApiError && err.status === 403
          ? t('notifications.cancelForbidden')
          : t('notifications.cancelFailed'),
      );
    });
  };

  return (
    <div className="rounded-md px-3 py-2">
      <div className="flex items-center gap-2">
        <ArrowPathIcon
          className={`h-4 w-4 shrink-0 text-brand ${job.status === 'running' ? 'motion-safe:animate-spin' : ''}`}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
          {t(TYPE_LABEL[job.type])}
        </span>
        <span className="shrink-0 text-xs text-muted">{t(STATUS_LABEL[job.status])}</span>
        {job.status === 'running' && canWrite && (
          <button
            type="button"
            onClick={onCancel}
            disabled={canceling}
            aria-label={t('notifications.cancel')}
            title={t('notifications.cancel')}
            className={`shrink-0 ${ICON_BUTTON_SIZE} rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-danger focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:opacity-50 motion-reduce:transition-none`}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Barre de progression : déterminée (done/total) ou indéterminée. */}
      <div className="mt-1.5 flex items-center gap-2">
        <div
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={determinate ? 100 : undefined}
          aria-valuenow={determinate ? percent : undefined}
          aria-label={t(TYPE_LABEL[job.type])}
        >
          <div
            className={`h-full rounded-full bg-brand ${determinate ? '' : 'w-1/3 motion-safe:animate-pulse'}`}
            style={determinate ? { width: `${percent}%` } : undefined}
          />
        </div>
        {determinate && (
          <span className="shrink-0 text-xs tabular-nums text-muted">
            {interpolate(t('notifications.progress'), { done: job.done, total: job.total }, locale)}
          </span>
        )}
      </div>

      {etaLabel !== null && (
        <p className="mt-1 text-xs text-muted">
          {interpolate(t('notifications.eta'), { eta: etaLabel })}
        </p>
      )}
      {started !== null && (
        <p className="mt-1 text-xs text-muted">
          {interpolate(t('notifications.startedAt'), { time: started })}
        </p>
      )}
      {cancelError !== null && (
        <p role="alert" className="mt-1 flex items-start gap-1 text-xs text-danger">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{cancelError}</span>
        </p>
      )}
    </div>
  );
}

/** Ligne d'un job terminé récent : icône de statut + texte + bouton supprimer. */
function RecentRow({
  job,
  onDismiss,
  onReview,
  onRefresh,
}: {
  job: Job;
  onDismiss: () => void;
  /** Présent uniquement pour un `ai_tag` terminé avec des propositions → « Réviser les tags ». */
  onReview?: (() => void) | undefined;
  /** Présent uniquement pour un `scan` terminé ayant indexé/retiré des fichiers → « Actualiser la galerie ». */
  onRefresh?: (() => void) | undefined;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  // « Relancer » (audit B3) : un job `failed` était TERMINAL — un scan de plusieurs
  // heures interrompu par un redémarrage (`failOrphanedRunning` finalise en échec tout
  // job resté `running`) n'avait aucune reprise. Le bouton n'apparaît que pour les
  // TYPES sûrs à rejouer (`isRetryableJobType`, dont la justification type par type vit
  // dans le contrat partagé) : proposer « Relancer » sur une rotation la composerait,
  // sur une purge de corbeille elle détruirait ce qui y a été mis depuis. Gardé aussi
  // par le rôle, comme « Annuler » : relancer est une écriture (403 sinon).
  const canWrite = useCanWrite();
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retryDone, setRetryDone] = useState(false);
  const canRetry =
    canWrite && job.status === 'failed' && isRetryableJobType(job.type) && !retryDone;

  const onRetry = (): void => {
    if (retrying) return;
    setRetryError(null);
    setRetrying(true);
    void retryJob(job.id)
      .then(() => {
        // Le nouveau job arrive par le flux SSE : on masque juste le bouton pour ne pas
        // enfiler deux fois d'affilée depuis la même ligne.
        setRetryDone(true);
      })
      .catch((err: unknown) => {
        // Échec JAMAIS silencieux, et message DISTINCT par cause (même discipline que
        // l'annulation) : un 403 lecture seule, un type refusé et un traitement déjà en
        // file sont trois situations différentes pour l'utilisateur.
        setRetryError(
          err instanceof ApiError && err.status === 403
            ? t('notifications.retryForbidden')
            : err instanceof ApiError && err.code === 'JOB_ALREADY_RUNNING'
              ? t('notifications.retryAlreadyRunning')
              : err instanceof ApiError && err.code === 'JOB_NOT_RETRYABLE'
                ? t('notifications.retryNotRetryable')
                : t('notifications.retryFailed'),
        );
      })
      .finally(() => setRetrying(false));
  };

  const terminal = job.status as 'done' | 'failed' | 'canceled';
  // Un job « Terminé » qui a en réalité TOUT échoué (ex. tagging IA : fournisseur
  // injoignable sur chaque photo) est affiché en AVERTISSEMENT — pas un faux succès vert.
  const degraded = isDoneButAllFailed(job);
  const Icon = degraded ? ExclamationTriangleIcon : STATUS_ICON[terminal];
  const tone = degraded ? 'text-warning-text' : STATUS_TONE[terminal];
  const statusLabel = degraded ? t('notifications.doneNoResult') : t(STATUS_LABEL[job.status]);
  const start = clock(job.startedAt);
  const end = clock(job.finishedAt);
  // Plage début → fin (permet de distinguer plusieurs scans successifs).
  const range =
    start !== null && end !== null
      ? interpolate(t('notifications.ranRange'), { start, end })
      : (end ?? start);
  // Résumé du RÉSULTAT (compress/move/trash_purge) sous la ligne de statut. Masqué
  // pour le cas « dégradé » (ai_tag tout échoué), qui a déjà son propre hint.
  const resultSummary = degraded ? null : formatJobResult(job, t, locale);

  return (
    <div className="flex items-center gap-2 rounded-md px-3 py-2">
      <Icon className={`h-4 w-4 shrink-0 ${tone}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-fg">{t(TYPE_LABEL[job.type])}</p>
        <p
          className={`flex items-center gap-1 text-xs ${degraded ? 'text-warning-text' : 'text-muted'}`}
        >
          <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{statusLabel}</span>
        </p>
        {degraded && (
          <p className="truncate text-xs text-warning-text">
            {t('notifications.doneNoResultHint')}
          </p>
        )}
        {resultSummary !== null && <p className="truncate text-xs text-muted">{resultSummary}</p>}
        {range !== null && <p className="truncate text-xs text-muted">{range}</p>}
        {/* Pont : réviser/valider les propositions IA (ouvre la galerie « Non taguées »). */}
        {onReview && (
          <button
            type="button"
            onClick={onReview}
            className="mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand transition-colors hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
          >
            <PencilSquareIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('notifications.reviewAiTags')}
          </button>
        )}
        {/* Pont : recharger la galerie après un scan de fond ayant indexé/retiré des fichiers. */}
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand transition-colors hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('notifications.refreshGallery')}
          </button>
        )}
        {/* Relancer un traitement en échec (audit B3). */}
        {canRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand transition-colors hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:opacity-50 motion-reduce:transition-none"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {retrying ? t('notifications.retrying') : t('notifications.retry')}
          </button>
        )}
        {retryDone && (
          <p role="status" className="mt-1 truncate text-xs text-brand">
            {t('notifications.retryQueued')}
          </p>
        )}
        {retryError !== null && (
          <p role="alert" className="mt-1 flex items-start gap-1 text-xs text-danger">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{retryError}</span>
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('notifications.dismiss')}
        title={t('notifications.dismiss')}
        className={`shrink-0 ${ICON_BUTTON_SIZE} rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none`}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
